import { useMutation, useQueryClient } from "@tanstack/react-query";
import { format, parseISO, startOfToday } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { trpc } from "@/utils/trpc";

const formSchema = z.object({
  managerEmail: z.string().email(),
  enabled: z.boolean().default(true),
  reason: z.string().trim().optional(),
  notes: z.string().trim().optional(),
  expiresAt: z.string().trim().optional(),
});

type FormValues = z.infer<typeof formSchema>;

const emptyForm: FormValues = {
  managerEmail: "",
  enabled: true,
  reason: "",
  notes: "",
  expiresAt: "",
};

export function SubscriptionBypassForm({
  triggerLabel = "Grant override",
}: {
  triggerLabel?: string;
}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState<FormValues>(emptyForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [expiresPickerOpen, setExpiresPickerOpen] = useState(false);

  const selectedExpiresDate = values.expiresAt
    ? parseISO(values.expiresAt)
    : undefined;

  const mutation = useMutation({
    ...trpc.admin.setSubscriptionOverride.mutationOptions(),
    onSuccess: () => {
      toast.success("Override updated");
      queryClient.invalidateQueries({
        queryKey: trpc.admin.listSubscriptionOverrides.queryKey(),
      });
      queryClient.invalidateQueries({
        queryKey: trpc.admin.summary.queryKey(),
      });
      queryClient.invalidateQueries({
        queryKey: trpc.admin.billingSummary.queryKey(),
      });
      setValues(emptyForm);
      setErrors({});
      setOpen(false);
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : "Unknown error";
      toast.error(message);
    },
  });

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setExpiresPickerOpen(false);
      if (!mutation.isPending) {
        setValues(emptyForm);
        setErrors({});
      }
    }
    setOpen(nextOpen);
  };

  const handleChange = (field: keyof FormValues, value: string | boolean) => {
    setValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrors({});

    const parsed = formSchema.safeParse(values);
    if (!parsed.success) {
      const fieldErrors: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const path = issue.path[0];
        if (typeof path === "string") {
          fieldErrors[path] = issue.message;
        }
      }
      setErrors(fieldErrors);
      return;
    }

    const payload = {
      managerEmail: parsed.data.managerEmail,
      enabled: parsed.data.enabled,
      reason: parsed.data.reason || undefined,
      notes: parsed.data.notes || undefined,
      expiresAt: parsed.data.expiresAt || undefined,
    };

    await mutation.mutateAsync(payload);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="secondary" size="sm">
          {triggerLabel}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Grant or update subscription override</DialogTitle>
          <DialogDescription>
            Provide the manager's email and optional context. Overrides grant
            full access without billing.
          </DialogDescription>
        </DialogHeader>
        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="space-y-2">
            <Label htmlFor="manager-email">Manager email</Label>
            <Input
              id="manager-email"
              value={values.managerEmail}
              onChange={(event) =>
                handleChange("managerEmail", event.target.value)
              }
              placeholder="manager@example.com"
              type="email"
              required
            />
            {errors.managerEmail ? (
              <p className="text-xs text-destructive">{errors.managerEmail}</p>
            ) : null}
          </div>

          <div className="flex items-center justify-between rounded-md border border-border/60 px-3 py-2">
            <Label htmlFor="override-enabled" className="flex-1">
              Enable override
            </Label>
            <Switch
              id="override-enabled"
              checked={values.enabled}
              onCheckedChange={(checked) => handleChange("enabled", checked)}
            />
          </div>
          {errors.enabled ? (
            <p className="text-xs text-destructive">{errors.enabled}</p>
          ) : null}

          <div className="space-y-2">
            <Label htmlFor="override-reason">Reason</Label>
            <Input
              id="override-reason"
              placeholder="Pilot cohort for ACME"
              value={values.reason ?? ""}
              onChange={(event) => handleChange("reason", event.target.value)}
            />
            {errors.reason ? (
              <p className="text-xs text-destructive">{errors.reason}</p>
            ) : null}
          </div>

          <div className="space-y-2">
            <Label htmlFor="override-notes">Notes</Label>
            <Textarea
              id="override-notes"
              placeholder="Internal context or links"
              value={values.notes ?? ""}
              onChange={(event) => handleChange("notes", event.target.value)}
              className="resize-none"
            />
            {errors.notes ? (
              <p className="text-xs text-destructive">{errors.notes}</p>
            ) : null}
          </div>

          <div className="space-y-2">
            <Label htmlFor="override-expires">Expires at (optional)</Label>
            <Popover
              open={expiresPickerOpen}
              onOpenChange={setExpiresPickerOpen}
            >
              <PopoverTrigger asChild>
                <Button
                  id="override-expires"
                  type="button"
                  variant="outline"
                  className={cn(
                    "h-10 w-full rounded-md justify-start text-left font-normal",
                    !selectedExpiresDate && "text-muted-foreground"
                  )}
                  aria-invalid={Boolean(errors.expiresAt)}
                >
                  <CalendarIcon className="mr-2 size-4" aria-hidden="true" />
                  {selectedExpiresDate
                    ? format(selectedExpiresDate, "MMM d, yyyy")
                    : "Select date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                side="bottom"
                sideOffset={4}
                className="w-auto p-0"
              >
                <Calendar
                  mode="single"
                  selected={selectedExpiresDate}
                  onSelect={(date) => {
                    handleChange(
                      "expiresAt",
                      date ? format(date, "yyyy-MM-dd") : ""
                    );
                    if (date) {
                      setExpiresPickerOpen(false);
                    }
                  }}
                  disabled={{ before: startOfToday() }}
                  initialFocus
                />
                <div className="border-t p-2 text-right">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      handleChange("expiresAt", "");
                      setExpiresPickerOpen(false);
                    }}
                  >
                    Clear date
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
            {errors.expiresAt ? (
              <p className="text-xs text-destructive">{errors.expiresAt}</p>
            ) : null}
          </div>

          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending
                ? "Saving…"
                : values.enabled
                  ? "Grant"
                  : "Disable"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
